import type { Monaco } from '@monaco-editor/react';
import useWatch from 'app/common/hooks/use-watch';
import type { editor } from 'monaco-editor';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { DragPreviewContext } from '../../../../schema-form/decorators/sortable-item.js';
import { getWidgetId } from '../../../../schema-form/util/widget.js';
import type { MonacoOwnProps } from './monaco.js';

type IStandaloneCodeEditor = editor.IStandaloneCodeEditor;

const DEFAULT_HEIGHT = 300;
const TITLE_HEIGHT = 32;
const INSTRUCTIONS_HEIGHT = 16;
const ERROR_HEIGHT = 40;

export const useMonaco = (props: MonacoOwnProps) => {
  const {
    onChange,
    onBlur,
    schema,
    config,
    error,
    value,
    disabled,
    resizable,
    monacoLanguage,
    editorInstanceRef: editorInstanceRefArg,
    monacoInstanceRef: monacoInstanceRefArg
  } = props;
  const isDragging = useContext(DragPreviewContext);

  const testId = useMemo(() => {
    return getWidgetId(props);
  }, [props]);

  // This is needed for dynamically resizing Monaco when using a flexible height.
  // For example, if the widget's error message appears and dissappears, this will
  // help Monaco resize correctly.
  const { height: detectedHeight = 0, ref: resizeDetectorRef } = useResizeDetector();

  const localEditorInstanceRef = useRef<editor.IStandaloneCodeEditor>();
  const localMonacoInstanceRef = useRef<Monaco>();

  const editorInstanceRef = editorInstanceRefArg ?? localEditorInstanceRef;
  const monacoInstanceRef = monacoInstanceRefArg ?? localMonacoInstanceRef;

  const monacoHeight = useMemo(() => {
    if (resizable) {
      return '100%';
    }

    const height: string | undefined = config.getIn(['options', 'height']);

    let basis;
    if (height?.endsWith('%')) {
      basis = detectedHeight;
    } else if (height?.endsWith('px')) {
      basis = Number(height.replace('px', ''));
    } else {
      basis = DEFAULT_HEIGHT;
    }

    let offset = 0;
    if (schema.getIn(['title'])) {
      offset += TITLE_HEIGHT;
    }

    if (config.getIn(['instructions'])) {
      offset += INSTRUCTIONS_HEIGHT;
    }

    if (error) {
      offset += ERROR_HEIGHT;
    }

    return `${basis - offset}px`;
  }, [resizable, config, schema, error, detectedHeight]);

  useWatch({
    value: Boolean(disabled),
    onChange: useCallback(
      (value: boolean) => {
        if (editorInstanceRef.current && monacoInstanceRef.current) {
          editorInstanceRef.current.updateOptions({ readOnly: value });
        }
      },
      [editorInstanceRef, monacoInstanceRef]
    )
  });

  const handleEditorWillMount = useCallback((monaco: Monaco) => {
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      enableSchemaRequest: false
    });
  }, []);

  const handleEditorDidMount = useCallback(
    (editor: IStandaloneCodeEditor, monaco: Monaco) => {
      editorInstanceRef.current = editor;
      monacoInstanceRef.current = monaco;
      const model = editor.getModel();
      if (model) {
        model.setValue(value as string);
      }

      editor.updateOptions({ readOnly: disabled });
    },
    [editorInstanceRef, monacoInstanceRef, disabled, value]
  );

  const handleChange = useCallback(
    (value?: string) => {
      // This is to ensure blur fires after change to correctly set dirty fields in the state
      onChange(value ?? '');
      onBlur();
    },
    [onBlur, onChange]
  );

  return {
    schema,
    config,
    error,
    testId,
    monacoHeight,
    resizeDetectorRef,
    resizable,
    monacoLanguage,
    handleEditorWillMount,
    handleEditorDidMount,
    handleChange,
    isDragging
  };
};

export type MonacoProps = ReturnType<typeof useMonaco>;
